import { Link } from "react-router-dom";
import { Checkbox, Image as AntImage, Typography } from "antd";
import type { StoneRequestMode } from "@gull/shared";
import ImageThumb from "../../components/ImageThumb";
import ReadOnlyField from "./ReadOnlyField";
import type { DraftItem, ShippingAddressDraft } from "./types";

const { Paragraph } = Typography;

export interface SummaryStepProps {
  name: string;
  email: string;
  phone: string;
  address: ShippingAddressDraft;
  bankAccountNumber: string;
  kontoEier: string;
  items: DraftItem[];
  orderImagePaths: string[];
  stoneMode: StoneRequestMode;
  selectedStoneItemIndexes: number[];
  salesTermsAccepted: boolean;
  onSalesTermsChange: (accepted: boolean) => void;
}

export default function SummaryStep({
  name,
  email,
  phone,
  address,
  bankAccountNumber,
  kontoEier,
  items,
  orderImagePaths,
  stoneMode,
  selectedStoneItemIndexes,
  salesTermsAccepted,
  onSalesTermsChange,
}: SummaryStepProps) {
  // Every mode but NONE_OR_KEEP removes stones from everything; SPECIFIC narrows it to a selection.
  const hasStoneRemoval = (index: number) =>
    stoneMode === "SPECIFIC"
      ? selectedStoneItemIndexes.includes(index)
      : stoneMode !== "NONE_OR_KEEP";

  return (
    <section className="sendin-panel">
      <h2>Oppsummering</h2>

      <div className="sendin-field-grid sendin-summary-fields">
        <ReadOnlyField label="Navn" value={name} />
        <ReadOnlyField label="Telefon" value={phone} />
        <ReadOnlyField label="E-post" value={email} wide />
        <ReadOnlyField label="Adresse" value={address.line1} wide />
        <ReadOnlyField label="Leilighet / c/o" value={address.line2} />
        <ReadOnlyField label="Postnummer" value={address.postalCode} />
        <ReadOnlyField label="Poststed" value={address.city} />
        <ReadOnlyField label="Kontonummer for utbetaling" value={bankAccountNumber} />
        <ReadOnlyField label="Eier av konto" value={kontoEier} />
      </div>

      {orderImagePaths.length ? (
        <div className="sendin-summary-images">
          <AntImage.PreviewGroup>
            {orderImagePaths.map((path) => (
              <ImageThumb key={path} path={path} />
            ))}
          </AntImage.PreviewGroup>
        </div>
      ) : null}

      <div className="sendin-field-grid sendin-summary-items">
        {items.map((item, index) => (
          <div className="sendin-input" key={index}>
            <strong>{item.description.trim() || `Gjenstand ${index + 1}`}</strong>
            {hasStoneRemoval(index) ? (
              <span className="sendin-summary-item-tag">Skånsom fjerning</span>
            ) : null}
          </div>
        ))}
      </div>

      <Paragraph className="sendin-stone-fee-note">
        Om du ikke ønsker å selge likevel tar vi et administrasjonsgebyr på 350kr, vi returnerer
        gjenstandene til deg i posten. Vi sender deg en kopi til din epost.
      </Paragraph>

      <Checkbox
        className="sendin-sales-terms"
        checked={salesTermsAccepted}
        onChange={(event) => onSalesTermsChange(event.target.checked)}
      >
        Jeg godtar og har forstått vilkår ved salg.{" "}
        <Link to="/vilkar" target="_blank" rel="noreferrer">
          Les fullstendige vilkår her.
        </Link>
      </Checkbox>
    </section>
  );
}
